import React, { useEffect, useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Cell, Row } from 'react-table';
import { useParentSizeContext, ColumnWithSorting, Loading, useTableColumnWidths } from '@tradingblock/components';
import { useDispatcher } from '../../../../data/global/hooks';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { AdminApplicationActions } from '../../../../data/global/actions/admin/AdminApplicationActions';
import { BlockTable } from '../../../shared/BlockTable';
import {
  ApplicationInvestigationUploadedDocument,
  ApplicationModel,
  ApplicationModelWithKey,
  ApplicationsRequest,
  ApplicationStatusApplicationDocuments,
  ApplicationStatusInvestigations,
  ApplicationStep,
  CurrentItemApplicationModel,
  HeardAboutInfo,
  RequestedInvestigationDocument,
} from '@tradingblock/types';
import { useApplicationsData } from '../useApplications';
import { isFetchingApplications } from '../../../../data/global/selectors/admin/applicationsSelector';
import { buildApiClient, useApi } from '../../../../context/Api';
import { ApplicationsActionCell } from './table/ApplicationsActionCell';
import { getDisplaySuffix } from '../../../../utilities/accountManagement';
import { AdminApplicationDetail } from '../AdminApplicationsDetail';
import { ApplicationsFilters } from './ApplicationsFilters';
import { useBlockTable } from '../../../shared/table/useBlockTableActions';

/** Table cell with async fetch — must be a real component so hooks run safely; cleanup avoids setState after unmount. */
const ApplicationsDocsRequiredCell: React.FC<{ row: Row<ApplicationModel> }> = ({ row }) => {
  const accountId = row.original.accountId;
  const signedNames = row.original.signedNames;
  const api = useApi();
  const [docsRequired, setDocsRequired] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        if (accountId && signedNames.length > 0) {
          const res = await api.application.getApplicationStatus(accountId);
          if (cancelled) {
            return;
          }
          if (res.responseCode === 0) {
            const { payload } = res;
            const { investigations, applications } = payload;
            const participants: {
              firstName: string;
              lastName: string;
              middleName: string;
              suffix: string;
              accountHolderId?: number;
              investigationId?: number;
              requestedCatagories: {
                application: RequestedInvestigationDocument[];
                investigations: RequestedInvestigationDocument[];
              };
              uploadedDocuments: ApplicationInvestigationUploadedDocument[];
            }[] = [];

            const applicationRequestedDocuments: (ApplicationStatusInvestigations &
              ApplicationStatusApplicationDocuments)[] = [
              ...(investigations ? investigations : []),
              ...(applications ? applications : []),
            ];

            applicationRequestedDocuments.forEach(requestedDocumentDetailEntry => {
              let participantIndex = participants.findIndex(
                participant =>
                  requestedDocumentDetailEntry.firstName === participant.firstName &&
                  requestedDocumentDetailEntry.lastName === participant.lastName
              );
              if (participantIndex === -1) {
                participants.push({
                  firstName: requestedDocumentDetailEntry.firstName ? requestedDocumentDetailEntry.firstName : '',
                  lastName: requestedDocumentDetailEntry.lastName ? requestedDocumentDetailEntry.lastName : '',
                  middleName: requestedDocumentDetailEntry.middleName ? requestedDocumentDetailEntry.middleName : '',
                  suffix: requestedDocumentDetailEntry.suffix ? requestedDocumentDetailEntry.suffix : '',
                  accountHolderId: requestedDocumentDetailEntry.accountHolderId,
                  investigationId: requestedDocumentDetailEntry.investigationId,
                  requestedCatagories: {
                    application: [],
                    investigations: [],
                  },
                  uploadedDocuments: [],
                });
                participantIndex = participants.length - 1;
              } else {
                if (!participants[participantIndex].accountHolderId && requestedDocumentDetailEntry.accountHolderId) {
                  participants[participantIndex].accountHolderId = requestedDocumentDetailEntry.accountHolderId;
                }
                if (participants[participantIndex].investigationId && requestedDocumentDetailEntry.investigationId) {
                  participants[participantIndex].investigationId = requestedDocumentDetailEntry.investigationId;
                }
              }

              const categories = [
                ...(requestedDocumentDetailEntry.appCategories ? requestedDocumentDetailEntry.appCategories : []),
                ...(requestedDocumentDetailEntry.cipCategories ? requestedDocumentDetailEntry.cipCategories : []),
              ];
              const uploadedDocumentsNames = [
                ...new Set(
                  requestedDocumentDetailEntry.uploadedDocuments
                    ? requestedDocumentDetailEntry.uploadedDocuments.map(document => document.documentName)
                    : []
                ),
              ];
              const uploadedDocuments = [
                ...new Set(
                  requestedDocumentDetailEntry.uploadedDocuments ? requestedDocumentDetailEntry.uploadedDocuments : []
                ),
              ];

              if (uploadedDocuments.length > 0) {
                participants[participantIndex].uploadedDocuments = uploadedDocuments;
              }

              const activeDocumentCategories = categories.filter(
                category =>
                  category.state !== 'Accepted' &&
                  category.state !== 'Pending' &&
                  !uploadedDocumentsNames
                    .map(uploadedDocumentName => category.requestedDocuments.includes(uploadedDocumentName))
                    .includes(true)
              );

              if (requestedDocumentDetailEntry.investigationId) {
                participants[participantIndex].requestedCatagories.investigations = activeDocumentCategories;
              } else {
                participants[participantIndex].requestedCatagories.application = activeDocumentCategories;
              }
            });

            const needsDocs = participants.some(
              participant =>
                participant.requestedCatagories.investigations.length > 0 ||
                participant.requestedCatagories.application.length > 0
            );
            setDocsRequired(needsDocs);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[ApplicationsDocsRequiredCell] getApplicationStatus failed:', error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [accountId, api, signedNames.join('|')]);

  if (isLoading) {
    return (
      <div className="text-center">
        <Loading size="small" />
      </div>
    );
  }

  return docsRequired ? <div className="neg text-center">Yes</div> : <div className="pos text-center">No</div>;
};

const AdminApplicationsColumns: ColumnWithSorting<ApplicationModel>[] = [
  {
    Header: 'AccountId',
    accessor: v => v.accountId,
    id: 'accountId',
    width: 0.5,
  },
  {
    Header: 'Primary Name',
    accessor: v => v.primaryAccountHolder.firstName && v.primaryAccountHolder.lastName,
    id: 'accountName',
    sortType: 'alphanumeric',
    Cell: ({ row }: Cell<any>) => {
      const { firstName, middleName, lastName, suffix } = row.original.primaryAccountHolder;
      if (!firstName || !lastName) {
        return <></>;
      } else {
        return <>{`${firstName} ${middleName ? middleName : ''} ${lastName} ${getDisplaySuffix(suffix)}`}</>;
      }
    },
  },
  {
    Header: 'Primary Email',
    accessor: v => v.primaryAccountHolder.email,
    id: 'email',
  },
  {
    Header: 'Username',
    accessor: v => v.ownerUsername,
    id: 'ownerUsername',
  },
  {
    Header: 'Type',
    accessor: v => v.type,
    id: 'type',
    width: 0.5,
  },
  {
    Header: 'Created On',
    accessor: v => v.createdOn,
    id: 'createdOn',
    width: 0.5,
    Cell: ({ row }: Cell<any>) => {
      const dateVal = dayjs(row.original.createdOn);
      if (dateVal && dateVal.isValid()) {
        return (
          <>
            {dateVal.format('M/DD/YY')}
            <br />
            {dateVal.format('hh:mm A')}
          </>
        );
      }
      return <></>;
    },
  },
  {
    Header: 'Last Saved',
    accessor: v => v.lastSavedOn,
    id: 'lastSavedOn',
    width: 0.5,
    Cell: ({ row }: Cell<any>) => {
      const dateVal = dayjs(row.original.lastSavedOn);
      if (dateVal && dateVal.isValid()) {
        return (
          <>
            {dateVal.format('M/DD/YY')}
            <br />
            {dateVal.format('hh:mm A')}
          </>
        );
      }
      return <></>;
    },
  },
  {
    Header: 'Latest Step',
    accessor: v => v.latestStepSaved,
    id: 'latestStepSaved',
    className: 'text-center',
    width: 0.5,
    Cell: ({ row }: Cell<any>) => {
      const lastStep = row.original.latestStepSaved;

      const isSubmitted =
        row.original.applicationSentOn !== null &&
        row.original.applicationSentOn !== undefined &&
        row.original.accountId !== null &&
        row.original.accountId !== undefined &&
        row.original.signedNames.length > 0 &&
        lastStep === ApplicationStep.Sign;

      let style =
        lastStep === 'sign' && isSubmitted
          ? 'pos text-center'
          : lastStep !== 'secure'
          ? 'pending text-center'
          : 'neg text-center';

      if (lastStep) {
        return (
          <div className={style}>
            {isSubmitted
              ? 'Submitted'
              : lastStep[0].toUpperCase() +
                lastStep
                  .slice(1)
                  .replace(/([A-Z])/g, ' $1')
                  .trim()}
          </div>
        );
      }
      return <></>;
    },
  },
  {
    Header: 'Docs Required',
    accessor: v => v.accountId,
    id: 'docsRequired',
    className: 'text-center',
    disableSortBy: true,
    width: 0.5,
    Cell: ({ row }: Cell<ApplicationModel>) => <ApplicationsDocsRequiredCell row={row} />,
  },
  {
    Header: 'Error Status',
    accessor: v => v.errorOnCreate,
    id: 'errorOnCreate',
    className: 'text-center',
    width: 0.5,
    disableSortBy: true,
    Cell: ({ row }: Cell<any>) => {
      const errorOnCreate = row.original.errorOnCreate;
      const lastStep = row.original.latestStepSaved;
      const isSubmitted =
        row.original.applicationSentOn !== null &&
        row.original.applicationSentOn !== undefined &&
        row.original.accountId !== null &&
        row.original.accountId !== undefined &&
        row.original.signedNames.length > 0 &&
        lastStep === ApplicationStep.Sign;
      // return errorOnCreate ? (
      //   <div className="neg text-center">Error</div>
      // ) : (
      //   <div className="pos text-center">Success</div>
      // );
      if (errorOnCreate && lastStep === ApplicationStep.Sign) {
        return <div className="neg text-center">Error</div>;
      }

      if (!errorOnCreate && isSubmitted) {
        return <div className="pos text-center">Success</div>;
      }

      return <div className="text-center">-</div>;
    },
  },
  {
    Header: 'Details',
    id: 'details',
    className: 'text-center',
    width: 0.5,
    Cell: ({ row }: Cell<any>) => {
      const application: CurrentItemApplicationModel = row.original;
      return (
        <div className="text-center">
          <ApplicationsActionCell {...application} />
        </div>
      );
    },
  },
];

export const ApplicationsTable = () => {
  const { dispatch } = useDispatcher();
  const { data, total, ...filterData } = useApplicationsData();
  const { page, pageSize, sortBy, sortByOrder } = filterData;
  const currentItemRequest = useStateSelector(s => s.private.applications.currentItem.application);
  const profile = useStateSelector(s => s.account.profile);
  const [isFetching, hasData] = useStateSelector(isFetchingApplications);
  const [heardAboutUsArray, setHeardAboutUsArray] = useState<HeardAboutInfo[]>([]);
  const tableState = useBlockTable('adminApplicationsTable');
  const api = buildApiClient();

  const heardAbout = async (siteIdConfig?: number) => {
    return await api.application.getHeardAbout(siteIdConfig);
  };

  function tryParseInt(value: string | number, defaultValue: number | null = null): number | null {
    // If value is already a number, return it
    if (typeof value === 'number') {
      return isNaN(value) ? defaultValue : value;
    }
    // If value is a string, parse it
    if (typeof value === 'string') {
      const result = parseInt(value, 10);
      return isNaN(result) ? defaultValue : result;
    }
    // For any other type, return default value
    return defaultValue;
  }

  function extractValue(s: string): string {
    const match = s.match(/\(([^)]+)\)/);
    return match ? match[1] : '';
  }

  const findRepDescription = useCallback(
    (hearAboutUsId: any, repId: any) => {
      const numberHearAboutUsId = tryParseInt(hearAboutUsId);
      const numberRepId = tryParseInt(repId);
      const heardAboutObject = heardAboutUsArray.find(obj => obj.heardAboutId === numberHearAboutUsId);
      let heardAboutUsDescription = '';
      let repCodeDescription = '';
      if (heardAboutObject) {
        if (heardAboutObject.representatives) {
          const repIdObject = heardAboutObject.representatives.find(rep => rep.repId === numberRepId);
          if (repIdObject) repCodeDescription = extractValue(repIdObject.description);
        }
        heardAboutUsDescription = heardAboutObject.description;
      }

      return [heardAboutUsDescription, repCodeDescription];
    },
    [heardAboutUsArray]
  );

  const columns = useMemo(() => {
    const repCodeColumn = {
      Header: 'Rep Code',
      id: 'repCode',
      width: 0.5,
      Cell: ({ row }: Cell<any>) => {
        const descriptions = findRepDescription(row.original.hearAboutUsId, row.original.repId);
        // Show the rep code if found, otherwise show the default value 'ABZ'
        return <>{descriptions[1] ? descriptions[1] : 'ABZ'}</>;
      },
    };

    // Always create the columns with the rep code column included
    const newColumns = [...AdminApplicationsColumns];
    newColumns.splice(1, 0, repCodeColumn);
    return newColumns;
  }, [findRepDescription]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      let combinedResult: any[] = [];

      try {
        // Try to fetch TB data
        const resultTB = await heardAbout(2);
        if (resultTB.responseCode === 0 && resultTB.payload) {
          combinedResult = [...combinedResult, ...resultTB.payload];
        } else {
          console.warn('TB heard about API request failed or returned no data');
        }
      } catch (error) {
        console.error('Error fetching TB heard about data:', error);
      }

      try {
        // Try to fetch MB data
        const resultMB = await heardAbout(4);
        if (resultMB.responseCode === 0 && resultMB.payload) {
          combinedResult = [...combinedResult, ...resultMB.payload];
        } else {
          console.warn('MB heard about API request failed or returned no data');
        }
      } catch (error) {
        console.error('Error fetching MB heard about data:', error);
      }

      if (cancelled) {
        return;
      }

      // Set the combined data even if only one request succeeded
      if (combinedResult.length > 0) {
        setHeardAboutUsArray(combinedResult);
      } else {
        console.warn('No heard about data could be loaded from either API endpoint');
        setHeardAboutUsArray([]);
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const columnWidths = useTableColumnWidths(columns as any, tableState.state.columnOrder);

  const visibleRepIds =
    profile && profile.current && profile.current.visibleRepIds ? profile.current.visibleRepIds : [];

  useEffect(() => {
    if (profile && profile.current && !isFetching) {
      dispatch(AdminApplicationActions.requestApplications({ ...filterData, repId: visibleRepIds }));
    }
  }, []);

  useEffect(() => {
    if (profile && profile.current && !isFetching) {
      const visibleRepIds = profile.current.visibleRepIds ? profile.current.visibleRepIds : [];
      dispatch(AdminApplicationActions.requestApplications({ ...filterData, repId: visibleRepIds }));

      const intervalId = setInterval(function() {
        dispatch(AdminApplicationActions.refreshApplications({ ...filterData, repId: visibleRepIds }));
      }, 60 * 3 * 1000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [profile]);

  const getCurrentSortBy = useCallback(
    (newDesc?: boolean) => {
      if (sortBy) {
        return { id: sortBy, desc: !_.isUndefined(newDesc) ? newDesc : sortByOrder === 'desc' };
      }
      return undefined;
    },
    [sortBy, sortByOrder]
  );

  const initialState = useMemo(() => {
    const currentSortBy = getCurrentSortBy();
    return {
      sortBy: currentSortBy ? [currentSortBy] : [],
      pageSize: _.isUndefined(pageSize) ? 10 : pageSize,
      pageIndex: page || 0,
      columnOrder: [],
    };
  }, [page, pageSize, getCurrentSortBy]);

  const pageCount = useMemo(() => {
    if (pageSize) {
      return Math.ceil(total / pageSize);
    }
    return 0;
  }, [pageSize, total]);

  const onSort = useCallback(
    (sort: any[]) => {
      const sortVal = _.first(sort) || getCurrentSortBy(sortByOrder === 'asc');
      if (sortVal) {
        const { id, desc } = sortVal;
        const sortData: Pick<ApplicationsRequest, 'sortBy' | 'sortByOrder'> = {
          sortBy: id,
          sortByOrder: desc === true ? 'desc' : 'asc',
        };
        dispatch(AdminApplicationActions.setAppSortBy(sortData));
      } else {
        dispatch(AdminApplicationActions.setAppSortBy({ sortBy: undefined, sortByOrder: undefined }));
      }
    },
    [getCurrentSortBy]
  );

  const onPage = useCallback((page: number) => {
    dispatch(AdminApplicationActions.setAppPage({ page }));
  }, []);

  const onPageSize = useCallback((pageSize: number) => {
    dispatch(AdminApplicationActions.setAppPageSize({ pageSize }));
  }, []);

  const getRowKey = useCallback((data: ApplicationModelWithKey) => `${data.entityKey}`, []);

  if (!_.isEmpty(currentItemRequest)) {
    return <AdminApplicationDetail heardAboutUsArray={heardAboutUsArray} />;
  }

  return (
    <>
      {isFetching && <Loading />}
      <useParentSizeContext.Provider>
        {!isFetching && (
          <>
            <ApplicationsFilters heardAboutUsArray={heardAboutUsArray} />
            {hasData && (
              <BlockTable
                tableKey="adminApplicationsTable"
                columns={columns}
                numColumns={columns.length}
                columnWidths={columnWidths}
                initialState={initialState}
                data={data}
                loaded={hasData}
                onSort={onSort}
                getRowKey={getRowKey}
                sortable
                hasPagination
                manualSortBy
                manualPagination
                pageCount={pageCount}
                onPage={onPage}
                onPageSize={onPageSize}
              />
            )}
          </>
        )}
        {!isFetching && !hasData && (
          <>
            <p>
              <i className="fal fa-user-alt-slash" /> No applications found
            </p>
          </>
        )}
      </useParentSizeContext.Provider>
    </>
  );
};
